import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Route, ChevronDown, BookOpen, Loader2, Eye, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getBookName } from '../utils/bookTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StudyPathViewer — public viewer for a shared study path (a chain of
 * cross-reference jumps). Renders each Book/Chapter step and lets the visitor
 * open any step, or load the whole path into their Bible reader trail.
 */
const StudyPathViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND_URL}/api/study-paths/${id}`)
      .then((r) => { if (!cancelled) setPath(r.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  const openStep = (s) => navigate(`/bible/${encodeURIComponent(s.book)}/${s.chapter}`);

  const openWholePath = () => {
    try { sessionStorage.setItem('study_trail', JSON.stringify(path.steps)); } catch (_) { /* quota */ }
    const first = path.steps[0];
    navigate(`/bible/${encodeURIComponent(first.book)}/${first.chapter}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/bible/John/3')} className="mb-4" data-testid="study-path-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> {es ? 'Ir al lector' : 'Go to reader'}
        </Button>

        <Card className="border-blue-200/70 dark:border-blue-800/50 shadow-lg" data-testid="study-path-viewer">
          <CardHeader>
            <div className="flex items-center gap-2 text-blue-600 dark:text-blue-300">
              <Route className="w-5 h-5" />
              <span className="text-[11px] font-semibold uppercase tracking-widest">
                {es ? 'Ruta de Estudio' : 'Study Path'}
              </span>
            </div>
            <CardTitle className="text-2xl" data-testid="study-path-title">
              {loading ? '…' : (path?.title || 'Study Path')}
            </CardTitle>
            {path?.author_name && (
              <p className="text-sm text-muted-foreground flex items-center gap-3">
                <span>{es ? 'por' : 'by'} {path.author_name}</span>
                <span className="inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {path.views}</span>
              </p>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" /></div>
            ) : error || !path ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="study-path-error">
                {es ? 'Esta ruta de estudio no existe o fue eliminada.' : "This study path doesn't exist or was removed."}
              </p>
            ) : (
              <>
                <ol className="space-y-1">
                  {path.steps.map((s, i) => (
                    <li key={`${s.book}-${s.chapter}-${i}`}>
                      <button
                        onClick={() => openStep(s)}
                        className="w-full flex items-center gap-3 rounded-lg border border-transparent hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 px-3 py-2 text-left transition-colors"
                        data-testid={`study-path-step-${i}`}
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-[11px] font-bold">
                          {i + 1}
                        </span>
                        <span className="flex-1 font-medium text-foreground">
                          {getBookName(s.book, language)} {s.chapter}
                        </span>
                        <BookOpen className="w-4 h-4 text-blue-400" />
                      </button>
                      {i < path.steps.length - 1 && (
                        <div className="flex justify-start pl-6">
                          <ChevronDown className="w-3.5 h-3.5 text-blue-300" />
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
                <Button
                  onClick={openWholePath}
                  className="w-full mt-5 bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="study-path-open-all"
                >
                  <Route className="w-4 h-4 mr-2" />
                  {es ? 'Abrir la ruta completa en el lector' : 'Open full path in reader'}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudyPathViewer;
